<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import FaqItems from "@components/FaqItems.svelte";
	import InfoCard from "@components/InfoCard.svelte";
	import Ol from "@components/Ol.svelte";
	import { OlSize } from "@components/OlProps.js";
	import Pill from "@components/Pill.svelte";
	import { PillColor, PillSize } from "@components/PillProps.js";
	import ReferenceContents from "@components/ReferenceContents.svelte";
	import Section from "@components/Section.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import { ReferencePlacement } from "@core/schema/ReferencePlacement.js";
	import { getClientsPath } from "@core/schema/paths/getClientsPath.js";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getReferencePath } from "@core/schema/paths/getReferencePath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getFaqsCloudSolutions } from "../data/getFaqsCloudSolutions.js";
	import { getReasonsCloudSolutions } from "../data/getReasonsCloudSolutions.js";
	import { getServicesCloud } from "../data/getServicesCloud.js";
	import type { CloudSolutionsProps } from "./CloudSolutionsProps.js";

	const _: CloudSolutionsProps = $props();
	const { tenant } = getGlobalTemplateProps();
	const clouds = ["AWS", "GCP", "Linux", "Azure"];
</script>

<Layout withStructuredData>
	<Section
		paddingFollowNext
		background={Background.LightGradient}
		centered
		gridHeadline="container-6"
		gridDesc="container-6"
		hero
	>
		{#snippet headline()}
			<Formatted text="Specializujeme se na bezpečná cg[[cloudová řešení]] pro firmy" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Vyvíjíme aplikace přímo v cloudu. Vyřešíme za vás i návrh cloudové infrastruktury nebo přechod na vzdálené úložiště. sp[[Na prvním místě je pro nás bezpečnost vašich dat.]]"
				/>
			</p>
		{/snippet}
		{#snippet cta()}
			<Link
				variant={ButtonVariant.PhloxOutlineLightBg}
				class="flex items-center justify-center mx-auto mt-2"
				href={getConnectWithFastestPath(tenant)}
			>
				<Formatted text="Potřebujeme vaši pomoc" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
	</Section>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="Cloudová řešení cg[[snižují]] náklady a cg[[zvyšují]] IT bezpečnost" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Proč riskovat a neefektivně ukládat data na pevná úložiště ve firmě? Když firemní systémy nebo aplikace přesunete do cloudu, sp[[budou v bezpečí a zároveň k nim získáte přístup kdykoliv a odkudkoliv na světě.]] "
				/>
			</p>
			<p>
				<Formatted
					text="Kromě výrazně lepšího kybernetického zabezpečení se můžete těšit i na sp[[nižší náklady na provoz úložišť a spolehlivé zálohování dat.]]"
				/>
			</p>
		{/snippet}
	</Section>

	<Section background={Background.LightGradient} noPadding="bot" gridHeadline="container-8 lg:text-center">
		{#snippet headline()}
			<Formatted text="S cg[[čím]] vám budeme cg[[k ruce]]?" />
		{/snippet}
	</Section>
	<Section gridChildren="max-md:full-width" background={Background.White} noPadding="both">
		<div
			class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 max-lg:full-width lg:px-1 bg-fstlight-100 border-t-4 border-fstlight-100"
		>
			{#each getServicesCloud(tenant) as { title, desc }, index (index)}
				<InfoCard {title} {desc} />
			{/each}
		</div>
	</Section>
	<Link
		variant={ButtonVariant.Phlox}
		class="flex items-center justify-center mx-auto mt-8 lg:mt-12"
		href={getConnectWithFastestPath(tenant)}
	>
		<Formatted text="Zeptat se na podrobnosti" />
		<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
	</Link>

	<Section
		paddingFollowNext
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		gridChildren="container-12"
	>
		{#snippet headline()}
			<Formatted text="cg[[6 důvodů]], proč vsadit na cloudová řešení" />
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={getReasonsCloudSolutions(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}

		<Link
			variant={ButtonVariant.Phlox}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
			>Chci fastest služby
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="Spoléháme na cg[[spolehlivé]] a cg[[moderní]] cloudové technologie" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Pracujeme s ověřenými, moderními a bezpečnými technologiemi, které nabízí ideální poměr cena/výkon. sp[[Cloud-based development děláme primárně na cloudu AWS (Amazon Web Services)]], ale používáme i další nástroje."
				/>
			</p>
			<ul class="flex gap-1 sm:gap-2 flex-wrap">
				{#each clouds as label (label)}
					<Pill size={PillSize.Small} color={PillColor.White}>{label}</Pill>
				{/each}
			</ul>
		{/snippet}
	</Section>

	<Section gridHeadline="container-8" centered>
		{#snippet headline()}
			<Formatted text="Vývoj na cloudu děláme pro firmy cg[[napříč obory]]" />
		{/snippet}
		<div class="mt-12">
			<ReferenceContents limit={8} placement={ReferencePlacement.CloudSolution} />
		</div>
		<Link
			variant={ButtonVariant.GreenOutlineLightBg}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getReferencePath(tenant)}
			>Zobrazit více
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section centered gridHeadline="container-6">
		{#snippet headline()}
			<Formatted text="Často kladené otázky ke cg[[cloudovým řešením]]" />
		{/snippet}
		<FaqItems faqs={getFaqsCloudSolutions(tenant)} />
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera]]} />
</Layout>
